// UI sound layer: tiny synthesized cyber blips (WebAudio oscillators — no asset
// files, no R2 sync lane). Wired ONCE globally via document-level delegation
// (initUiSfx), so every button / tab / link across the app gets hover + press
// feedback without per-component wiring. Gated on the same SFX settings as the
// duel sound effects. A container can opt its subtree out with data-sfx="off".
//
// Browsers keep the AudioContext suspended until a user gesture, so hover blips
// are silently dropped until the first pointerdown/keydown unlocks audio.

import { settings } from "./stores/settings.svelte.ts";

let ctx: AudioContext | null = null;
let master: GainNode | null = null;

function ensureCtx(): AudioContext | null {
  if (!ctx) {
    try {
      ctx = new AudioContext();
    } catch {
      return null; // no WebAudio → UI stays silent
    }
    master = ctx.createGain();
    master.connect(ctx.destination);
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** One enveloped oscillator: freq sweep + fast exponential decay (a "blip"). */
function blip(
  freq: number,
  freqEnd: number,
  dur: number,
  vol: number,
  type: OscillatorType = "sine",
  delay = 0,
): void {
  if (!settings.sfxEnabled || settings.sfxVolume <= 0) return;
  const c = ensureCtx();
  if (!c || c.state !== "running" || !master) return;
  const t0 = c.currentTime + delay;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t0);
  o.frequency.exponentialRampToValueAtTime(Math.max(1, freqEnd), t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(vol * settings.sfxVolume, t0 + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g).connect(master);
  o.start(t0);
  o.stop(t0 + dur + 0.02);
}

/** Soft high tick — pointer enters an interactive element. */
export function uiHover(): void {
  blip(2600, 3400, 0.045, 0.05);
}

/** Digital affirm — pressing an interactive element (a body + a bright transient). */
export function uiPress(): void {
  blip(680, 520, 0.07, 0.1, "triangle");
  blip(2400, 2000, 0.045, 0.045, "sine", 0.008);
}

// ── global wiring ────────────────────────────────────────────────────────────
const SEL = 'button, a, select, summary, [role="button"], [role="tab"], [role="checkbox"], input[type="range"]';

function interactive(el: EventTarget | null): HTMLElement | null {
  if (!(el instanceof Element)) return null;
  const m = el.closest<HTMLElement>(SEL);
  if (!m || m.closest('[data-sfx="off"]')) return null;
  if ((m as HTMLButtonElement).disabled || m.getAttribute("aria-disabled") === "true") return null;
  return m;
}

let lastHover = 0;

/** Install the document-level listeners. Call once at boot. */
export function initUiSfx(): void {
  document.addEventListener(
    "pointerover",
    (e) => {
      if (e.pointerType === "touch") return; // no hover on touch
      const m = interactive(e.target);
      // Ignore moves between children of the same interactive element.
      if (!m || (e.relatedTarget instanceof Node && m.contains(e.relatedTarget))) return;
      // A container can keep PRESS feedback but silence hover blips (data-sfx="hover-off") —
      // sweeping across a fanned duel hand machine-gunned ticks on every card (owner report).
      if (m.closest('[data-sfx="hover-off"]')) return;
      const now = performance.now();
      if (now - lastHover < 60) return; // fast sweeps across a menu → don't machine-gun
      lastHover = now;
      uiHover();
    },
    { capture: true, passive: true },
  );
  document.addEventListener(
    "pointerdown",
    (e) => {
      ensureCtx(); // a real user gesture: unlock audio for everything after
      if (interactive(e.target)) uiPress();
    },
    { capture: true, passive: true },
  );
  document.addEventListener(
    "keydown",
    (e) => {
      if ((e.key === "Enter" || e.key === " ") && !e.repeat && interactive(e.target)) uiPress();
    },
    true,
  );
}
